<script setup lang="ts">
import {PropType, onMounted, ref} from "vue";
import {BulkEntityApplication} from "@/domains/home/services/nir/registration/types";
import {useFormsStore} from "@/domains/home/services/nir/configurations/stores/forms.ts";
import {useInstitutionStore} from "@/domains/home/services/nir/associate-registers/stores";
import AppModal from "@/components/ui/AppModal.vue";

const props = defineProps({
  data: Object as PropType<BulkEntityApplication>,
})

const formStore = useFormsStore()
const institutionStore = useInstitutionStore()
const loading = ref(true)
const errorsModalOpen = ref(false)

onMounted(() => {
  institutionStore.fetchInstitutions(1, 1000)
  if (props.data?.formId) {
    formStore.fetchForm(props.data.formId)
        .then(() => {
          loading.value = false
        })
  } else {
    loading.value = false
  }
})

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleString()
}
</script>

<template>
  <div class="flex flex-col">
    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center py-12">
      <i class="fa fa-spinner fa-spin text-2xl text-primary mr-2"></i>
      <span class="text-gray-600">Loading details...</span>
    </div>

    <!-- Application Details -->
    <div v-else class="flex flex-col bg-white rounded-lg shadow p-6" id="details">
      <h3 class="text-xl font-semibold text-gray-900 mb-4 border-b pb-2">
        <i class="fa fa-info-circle text-primary mr-2"></i>
        Bulk Application Details
      </h3>

      <div v-if="props.data" class="grid grid-cols-2 gap-4">
        <!-- Tracking Number -->
        <div v-if="props.data.trackingNumber" class="flex flex-col">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Tracking Number</label>
          <p class="text-sm text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded">{{ props.data.trackingNumber }}</p>
        </div>

        <!-- Type -->
        <div v-if="props.data.type" class="flex flex-col">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Type</label>
          <p class="text-sm text-gray-900">{{ props.data.type }}</p>
        </div>

        <!-- Sub Type -->
        <div v-if="props.data.subType" class="flex flex-col">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Sub Type</label>
          <p class="text-sm text-gray-900">{{ props.data.subType }}</p>
        </div>

        <!-- Form -->
        <div v-if="props.data.formId" class="flex flex-col">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Form</label>
          <p class="text-sm text-gray-900">{{ formStore.form?.name || 'N/A' }}</p>
        </div>

        <!-- Institution -->
        <div v-if="props.data.institutionId" class="flex flex-col">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Institution</label>
          <p class="text-sm text-gray-900">
            {{ institutionStore.institutions?.find(i => i.id === props.data?.institutionId)?.name || 'N/A' }}
          </p>
        </div>

        <!-- Data File ID -->
        <div v-if="props.data.dataFileId" class="flex flex-col">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Data File ID</label>
          <p class="text-sm text-gray-900 font-mono text-xs bg-gray-50 px-2 py-1 rounded truncate" :title="props.data.dataFileId">
            {{ props.data.dataFileId }}
          </p>
        </div>

        <!-- Status -->
        <div v-if="props.data.status" class="flex flex-col">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Status</label>
          <span class="text-sm font-medium px-3 py-1 rounded-full w-fit"
                :class="{
                  'bg-gray-100 text-gray-600': props.data.status === 'PENDING',
                  'bg-blue-100 text-blue-800': props.data.status === 'SUBMITTED',
                  'bg-yellow-100 text-yellow-800': props.data.status === 'PROCESSING',
                  'bg-primary-100 text-primary-800': props.data.status === 'ASSIGNED',
                  'bg-green-100 text-green-800': props.data.status === 'APPROVED' || props.data.status === 'COMPLETED',
                  'bg-red-100 text-red-800': props.data.status === 'REJECTED' || props.data.status === 'FAILED'
                }">
            <i :class="{
              'fa fa-clock': props.data.status === 'PENDING',
              'fa fa-paper-plane': props.data.status === 'SUBMITTED',
              'fa fa-cog fa-spin': props.data.status === 'PROCESSING',
              'fa fa-user-check': props.data.status === 'ASSIGNED',
              'fa fa-check-circle': props.data.status === 'APPROVED' || props.data.status === 'COMPLETED',
              'fa fa-times-circle': props.data.status === 'REJECTED' || props.data.status === 'FAILED'
            }" class="mr-1"></i>
            {{ props.data.status }}
          </span>
        </div>

        <!-- Created At -->
        <div v-if="props.data.createdAt" class="flex flex-col">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Created At</label>
          <p class="text-sm text-gray-900">
            <i class="fa fa-calendar-alt text-gray-400 mr-1"></i>
            {{ formatDate(props.data.createdAt) }}
          </p>
        </div>

        <!-- Updated At -->
        <div v-if="props.data.updatedAt" class="flex flex-col">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Updated At</label>
          <p class="text-sm text-gray-900">
            <i class="fa fa-clock text-gray-400 mr-1"></i>
            {{ formatDate(props.data.updatedAt) }}
          </p>
        </div>

        <!-- Message -->
        <div class="flex flex-col col-span-2">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Message</label>
          <p class="text-sm bg-gray-50 px-3 py-2 rounded border border-gray-200"
             :class="props.data.message ? 'text-gray-900' : 'text-gray-400 italic'">
            <i class="fa fa-info-circle text-gray-400 mr-1"></i>
            {{ props.data.message || 'N/A' }}
          </p>
        </div>

        <!-- Errors -->
        <div class="flex flex-col col-span-2">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Errors</label>
          <button
              v-if="props.data.errors && props.data.errors.length > 0"
              type="button"
              @click="errorsModalOpen = true"
              class="inline-flex items-center gap-2 text-sm text-danger-700 bg-danger-50 hover:bg-danger-100 border border-danger-200 px-3 py-2 rounded w-fit transition">
            <i class="fa fa-triangle-exclamation"></i>
            View {{ props.data.errors.length }} error{{ props.data.errors.length === 1 ? '' : 's' }}
          </button>
          <p v-else class="text-sm text-gray-400 italic bg-gray-50 px-3 py-2 rounded border border-gray-200 w-fit">
            <i class="fa fa-circle-check text-gray-400 mr-1"></i>
            No errors reported
          </p>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="text-center py-8 text-gray-500">
        <i class="fa fa-inbox text-4xl mb-2"></i>
        <p>No application details available</p>
      </div>
    </div>
  </div>

  <AppModal v-model="errorsModalOpen" xl2>
    <div class="flex flex-col p-4">
      <h3 class="text-lg font-semibold text-gray-900 mb-3 border-b pb-2 flex items-center gap-2">
        <i class="fa fa-triangle-exclamation text-danger"></i>
        Bulk Application Errors
        <span v-if="props.data?.errors" class="text-sm text-gray-500 font-normal">
          ({{ props.data.errors.length }})
        </span>
      </h3>
      <ol class="flex flex-col gap-2 max-h-[60vh] overflow-y-auto">
        <li
            v-for="(err, idx) in props.data?.errors || []"
            :key="idx"
            class="text-sm text-danger-800 bg-danger-50 border border-danger-200 rounded px-3 py-2 flex gap-2">
          <span class="font-bold text-danger-600 shrink-0">{{ idx + 1 }}.</span>
          <span class="break-words">{{ err }}</span>
        </li>
      </ol>
    </div>
  </AppModal>
</template>

<style scoped>

</style>